import * as React from "react";
import { useState, useMemo, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CircularProgress from "@mui/material/CircularProgress";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useToken } from "../items/UseToken";
import { IconButton } from "@mui/material";
import { VolumeUp } from "@mui/icons-material";
import baseVoicesData from "../items/voicesDb";
import { useParams } from 'react-router';
import { useNavigate } from "react-router-dom";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import HeygenSelection from "./AgentAvatarComponents/HeygenSelection";
import { GoogleLoginButton } from "./ResourcesComponents/GoogleLoginButton";
import { TextareaAutosize } from "@mui/material";
import ListSubheader from "@mui/material/ListSubheader";
import ApiKeySelector from "../ApiKeySelector";
import GlassPanel from "../Common/GlassPanel";
import NiceSlider from "../Common/NiceSlider";
import {
    Tooltip,
    Checkbox,
} from "@mui/material";
import AgentFlowCreator from "./AgentSkillMapBuilder";

const steps = [
    "Name & Appearance",
    "Personality Traits",
    "Intelligence Settings",
    "Voice Configuration",
    "Skill Map",
    "Assign Resources"
];

export const languagesArray = [
    // 🏁 najczęstsze
    { code: "en", label: "English" },
    { code: "pl", label: "Polish" },

    // 🇪🇺 języki europejskie
    { code: "de", label: "German" },
    { code: "fr", label: "French" },
    { code: "es", label: "Spanish" },
    { code: "it", label: "Italian" },
    { code: "pt", label: "Portuguese" },
    { code: "nl", label: "Dutch" },
    { code: "da", label: "Danish" },
    { code: "sv", label: "Swedish" },
    { code: "no", label: "Norwegian" },
    { code: "fi", label: "Finnish" },
    { code: "cs", label: "Czech" },
    { code: "hu", label: "Hungarian" },
    { code: "ro", label: "Romanian" },
    { code: "sk", label: "Slovak" },
    { code: "bg", label: "Bulgarian" },
    { code: "hr", label: "Croatian" },
    { code: "sr", label: "Serbian" },
    { code: "sl", label: "Slovenian" },
    { code: "el", label: "Greek" },
    { code: "uk", label: "Ukrainian" },
    { code: "ru", label: "Russian" },

    // 🌍 pozostałe
    { code: "ar", label: "Arabic" },
    { code: "he", label: "Hebrew" },
    { code: "hi", label: "Hindi" },
    { code: "th", label: "Thai" },
    { code: "vi", label: "Vietnamese" },
    { code: "tr", label: "Turkish" },
    { code: "ko", label: "Korean" },
    { code: "ja", label: "Japanese" },
    { code: "zh", label: "Chinese" },

    // 🌐 specjalna kategoria
    { code: "multi", label: "Multilingual" },
];

export default function AgentWizardStepper({ handleDialogClose }) {



    const { token } = useToken()
    const { agentGuid } = useParams();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = React.useState(steps[0]);
    const [agentName, setAgentName] = React.useState("");
    const [title, setTitle] = React.useState("");
    const [faceImage, setFaceImage] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [machines, setMachines] = React.useState([]); // Fetched machines
    const [selectedMachine, setSelectedMachine] = React.useState(null); // Fetched machines
    const [telephony, setTelephony] = React.useState([]);
    const [selectedAgent, setSelectedAgent] = React.useState(null);
    const [selectedTel, setSelectedTel] = React.useState(null);
    const [traits, setTraits] = React.useState({
        Humor: 50,
        Formality: 50,
        Empathy: 50,
    });
    const [jobDescription, setJobDescription] = React.useState("");
    const [systemPrompt, setSystemPrompt] = React.useState("");
    const [showApiKeysModal, setShowApiKeysModal] = React.useState(false);

    const [uploadedFile, setUploadedFile] = React.useState(null);
    const [voicesData, setVoicesData] = useState(baseVoicesData);
    const [voice, setVoice] = React.useState(voicesData["Azure"]["English"][0].value);
    const [voiceSpeed, setVoiceSpeed] = useState("fast");
    const [bargeInEnabled, setBargeInEnabled] = useState(false);
    const [ttsVendor, setTtsVendor] = React.useState("Azure");
    const [sttVendor, setSttVendor] = React.useState("Azure");
    const [language, setLanguage] = React.useState("English");
    const [temperature, setTemperature] = useState(0.80);
    const [model, setModel] = useState("gpt-4.1-mini");
    const [avatarType, setAvatarType] = React.useState("basic");
    const [heygenKey, setHeygenKey] = React.useState(null);
    const [selectedAvatar, setSelectedAvatar] = React.useState(null);
    const [googleEmail, setGoogleEmail] = React.useState(null);
    const [apiKeys, setApiKeys] = React.useState([]);
    const [selectedKeys, setSelectedKeys] = React.useState({});
    const [showAdvanced, setShowAdvanced] = React.useState(false);
    const [flowData, setFlowData] = useState(() => ({ roots: [] }));
    const [aiMode, setAiMode] = useState("live"); // "live" | "draft"

    const [error, setError] = React.useState(null); // Error handling
    const availableVoices = voicesData[ttsVendor]?.[language] || [];


    const llmModelArray = ["gpt-5.2", "gpt-4o-mini", , "gpt-4.1", "gpt-4.1-mini", "gpt-4.1-nano", "o3-mini"]
    const voiceSpeedArray = ["x-slow", "slow", "medium", "fast", "x-fast"]


    const getLanguageLabel = (codeOrLabel, languagesArray) => {
        const lower = codeOrLabel.toLowerCase();
        const prefix = lower.split("-")[0];
        const match = languagesArray.find(
            (l) =>
                l.code.toLowerCase() === prefix ||
                l.label.toLowerCase() === lower
        );
        return match ? match.label : codeOrLabel;
    };

    const sortedLanguages = React.useMemo(() => {
        const vendorData = voicesData?.[ttsVendor];
        if (!vendorData) return [];
        return Object.keys(vendorData).sort();
    }, [voicesData, ttsVendor]);

    const voiceListRef = useRef(null);
    const [savedScrollTop, setSavedScrollTop] = useState(0);

    useEffect(() => {
        if (voiceListRef.current) {
            voiceListRef.current.scrollTop = savedScrollTop;
        }
    }, [voice]); 
    useEffect(() => {
        if (!voice || activeStep !== "Voice Configuration") return;

        const timeout = setTimeout(() => {
            const el = document.getElementById(`voice-${voice}`);
            if (el) {
                el.scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                });
            }
        }, 1000); // mały delay żeby mieć pewność, że lista już jest

        return () => clearTimeout(timeout);
    }, [voice, activeStep, availableVoices.length, ttsVendor]);



    const fetchAzureVoices = async () => {
        try {
            const res = await fetch("/api/voices?vendor=Azure");



            if (!res.ok) throw new Error(/*`HTTP ${res.status}`*/);
            const data = await res.json();

            const grouped = {};

            // 🧠 Pomocnicza funkcja do czystego przypisania języka
            function getLanguageFromLocale(locale, isMultilingual) {
                if (isMultilingual) return "Multilingual";
                if (!locale) return "Unknown";

                const prefix = locale.split("-")[0];
                const match = languagesArray.find((lang) => lang.code === prefix);
                return match ? match.label : locale;
            }

            data.forEach((voice) => {
                if (!voice?.Locale) return;

                const shortName = voice.ShortName || voice.Name;
                if (!shortName) return;

                const isMultilingual =
                    voice.VoiceType?.includes("Multilingual") ||
                    shortName.includes("Multilingual");

                // 👇 Używamy helpera zamiast if-ów
                const lang = getLanguageFromLocale(voice.Locale, isMultilingual);

                const sampleUrl = `https://ai.azure.com/speechassetscache/ttsvoice/Masterpieces/${shortName.replace(
                    "Neural",
                    ""
                )}-General-audio.wav`;

                if (!grouped[lang]) grouped[lang] = [];

                grouped[lang].push({
                    name: voice.DisplayName || voice.LocalName || shortName,
                    value: shortName,
                    audioSrc: sampleUrl,
                });
            });

            setVoicesData((prev) => ({
                ...prev,
                Azure: grouped,
            }));

            /*console.log("✅ Updated voicesData.Azure:", grouped);*/
        } catch (err) {
           /* console.error("❌ Azure fetch error:", err);*/
        }
    };

    const fetchElevenLabsVoices = async () => {
        try {
            const res = await fetch("/api/voices?vendor=ElevenLabs");

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            const grouped = {};

            function getLanguageFromCode(code) {
                if (!code) return "Unknown";
                const prefix = code.split("-")[0].toLowerCase();
                const match = languagesArray.find(
                    (lang) => lang.code.toLowerCase() === prefix
                );
                return match ? match.label : code.toUpperCase();
            }

            // 🧩 Krok 1: grupowanie wg języka z metadanych
            data.voices.forEach((voice) => {
                const lang = getLanguageFromCode(voice.labels?.language || "Unknown");
                if (!grouped[lang]) grouped[lang] = [];

                grouped[lang].push({
                    name: voice.name,
                    value: voice.voice_id,
                    audioSrc: voice.preview_url || null,
                    gender: voice.labels?.gender || null,
                    accent: voice.labels?.accent || null,
                    category: voice.category,
                });
            });

            // 🧩 Krok 2: tworzymy “Multilingual” listę (wszystkie głosy)
            const allVoices = Object.values(grouped).flat();

            // 🧩 Krok 3: kopiujemy wszystkie głosy do KAŻDEGO języka
            languagesArray.forEach((lang) => {
                if (!grouped[lang.label]) grouped[lang.label] = [];
                // dodajemy wszystkie głosy, ale tylko te, których jeszcze nie ma
                const existingIds = new Set(grouped[lang.label].map((v) => v.value));
                allVoices.forEach((v) => {
                    if (!existingIds.has(v.value)) {
                        grouped[lang.label].push({ ...v });
                    }
                });
            });

            // 🧩 Krok 5: ustaw stan
            setVoicesData((prev) => ({
                ...prev,
                ElevenLabs: grouped,
            }));

            /*console.log("✅ Updated voicesData.ElevenLabs:", grouped);*/
        } catch (err) {
            /*console.error("❌ ElevenLabs fetch error:", err);*/
        }
    };

    

    const expandUniversalVoices = React.useCallback((providerName) => {
        setVoicesData((prev) => {
            const provider = prev[providerName];
            if (!provider) return prev;

            const allVoices = provider.Multilingual || [];
            const expanded = {};

            languagesArray.forEach((lang) => {
                expanded[lang.label] = allVoices.map((v) => ({
                    ...v,
                    provider: providerName,
                }));
            });

            return {
                ...prev,
                [providerName]: expanded,
            };
        });
    }, []);


    


    const useFetchedVendors = () => {
        const fetched = React.useRef(new Set());
        const markFetched = (vendorName) => fetched.current.add(vendorName);
        const hasFetched = (vendorName) => fetched.current.has(vendorName);
        return { hasFetched, markFetched };
    };

    const { hasFetched, markFetched } = useFetchedVendors();

    React.useEffect(() => {
        const fetchVoices = async () => {
            if (ttsVendor === "Azure" && !hasFetched("Azure")) {
                await fetchAzureVoices();
                markFetched("Azure");
            }
            else if (ttsVendor === "ElevenLabs" && !hasFetched("ElevenLabs")) {
                await fetchElevenLabsVoices();
                markFetched("ElevenLabs");
            }
            else if ((ttsVendor === "Realtime Api" || ttsVendor === "OpenAi")) {
                expandUniversalVoices(ttsVendor);
                setLanguage((prev) => prev || "English");
            }
            else if (ttsVendor === "Deepgram") {
                setLanguage("English")
            }
            
        };

        fetchVoices();
    }, [ttsVendor]);

 



    let currentAudio = null;

    // wyciąga nazwę głosu z URL
    const parseVoiceName = (url) => {
        const m = (url || "").match(/\/[A-Za-z]{2}-[A-Za-z]{2}-([A-Za-z]+)-General-[Aa]udio\.wav$/);
        return m ? m[1] : null;
    };

    const playAudio = (src, event) => {
        event?.stopPropagation?.();
        /*console.log("🎧 Próba odtworzenia:", src);*/

        // ⏹️ zatrzymaj poprzednie audio zawsze przed startem nowego
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            currentAudio = null;
        }

        const name = parseVoiceName(src);

        // 1️⃣ spróbuj z bazy
        const playFromDb = (onFail) => {
            const englishVoices = voicesData?.Azure?.English || [];
            const found = englishVoices.find(v => v.name.toLowerCase() === (name || "").toLowerCase());

            if (!found) {
                onFail?.();
                return;
            }

            const correctUrl = found.audioSrc
                .replace("en-US", "En-US")
                .replace("General-audio.wav", "General-Audio.wav")
                .replace("general-audio.wav", "General-Audio.wav")
                .trim();

            /*console.log(`💾 Z bazy: ${found.name} →`, correctUrl);*/
            startAudio(correctUrl, onFail);
        };

        // 2️⃣ fallbacki
        const tryPlay = (url, onFail) => {
            startAudio(url, onFail);
        };

        // 🔊 wspólna funkcja startu audio — gwarantuje jedno źródło naraz
        const startAudio = (url, onFail) => {
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
            }

            const audio = new Audio(url);
            currentAudio = audio;

            audio.addEventListener("ended", () => {
                if (currentAudio === audio) currentAudio = null;
            });

            audio.play()
                /*.then(() => console.log("🎶 Odtwarzanie:", url))*/
                .catch(err => {
                    /*console.warn("⚠️ Nie zagrało:", url, err);*/
                    if (currentAudio === audio) currentAudio = null;
                    onFail?.();
                });
        };

        // przygotuj fallbacki
        const fallbacks = [];
        if (src.includes("audio.wav")) fallbacks.push(src.replace("audio.wav", "Audio.wav"));
        else if (src.includes("Audio.wav")) fallbacks.push(src.replace("Audio.wav", "audio.wav"));

        if (src.includes("en-us")) {
            fallbacks.push(src.replace("en-us", "en-US"));
            fallbacks.push(src.replace("en-us", "En-US"));
        } else if (src.includes("en-US")) {
            fallbacks.push(src.replace("en-US", "en-us"));
            fallbacks.push(src.replace("en-US", "En-US"));
        } else if (src.includes("En-US")) {
            fallbacks.push(src.replace("En-US", "en-US"));
            fallbacks.push(src.replace("En-US", "en-us"));
        }

        if (src.includes("General-audio.wav")) fallbacks.push(src.replace("General-audio.wav", "General-Audio.wav"));
        else if (src.includes("General-Audio.wav")) fallbacks.push(src.replace("General-Audio.wav", "General-audio.wav"));

        const queue = Array.from(new Set([src, ...fallbacks])).filter(Boolean);

        // 🔁 kolejność: najpierw baza → potem fallbacki
        playFromDb(() => {
            const next = queue.shift();
            if (next) {
                tryPlay(next, () => {
                    const next2 = queue.shift();
                    if (next2) tryPlay(next2);
                    /*else console.error("❌ Wszystkie próby odtworzenia nieudane.");*/
                });
            } else {
                /*console.error("❌ Brak fallbacków.");*/
            }
        });
    };





    React.useEffect(() => {

        fetchAgent();
    }, [agentGuid]);

    React.useEffect(() => {
        fetchMachines();
        fetchTelephony();
    }, [activeStep]);

    // Step: Fetch AI-generated face image
    const fetchGeneratedFace = async () => {
        if (!agentName) return;
        setFaceImage(null);
        setLoading(true);
        try {

            const response = await fetch(`api/getlocalimage?name=${agentName}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            const jsonData = await response.json();
            const base64Data = jsonData.base64; // Base64 string
            const compressedImage = await compressImage(base64Data, 512, 512, 0.7);
            setFaceImage(compressedImage);
        } catch (error) {
            console.error("Error generating image:", error);
        } finally {
            setLoading(false);
        }
    };

    const compressImage = (base64Data, maxWidth, maxHeight, quality = 0.7) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = `data:image/png;base64,${base64Data}`;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                // Calculate new dimensions
                let { width, height } = img;
                if (width > maxWidth || height > maxHeight) {
                    const scale = Math.min(maxWidth / width, maxHeight / height);
                    width = Math.round(width * scale);
                    height = Math.round(height * scale);
                }

                // Set canvas dimensions and draw resized image
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to JPEG or reduced-quality PNG
                const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
                resolve(compressedDataUrl);
            };
        });
    };
    const extractTraits = (input) => {
        const regex = /(\w+) level (\d+)\/100/g;
        const traits = {};
        let match;

        while ((match = regex.exec(input)) !== null) {
            traits[match[1]] = parseInt(match[2], 10); // Convert to integer
        }

        return traits;
    };
    const fetchAgent = async () => {
        setLoading(true);
        setError(null);

        const queryParams = `&guid=${agentGuid}`;
        const query = "api/getdatabase?table_name=agents" + queryParams;

        try {
            const response = await fetch(query, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch agents");
            }

            const data = await response.json();
            var agent = data.results[0];
            setSelectedAgent(agent || null); // Assuming data contains `results`
            if (agent) {
                setAgentName(agent.name);
                setTitle(agent.title);
                setJobDescription(agent.job_description);
                setSystemPrompt(agent.system_prompt);
                setFaceImage(agent.picture);
                setLanguage(agent.language);
                if (agent.voice.includes("_")) {
                    var stt = agent.voice.split('_')[0];
                    var tts = agent.voice.split('_')[1];
                    var voiceModel = agent.voice.split('_')[2];
                    setSttVendor(stt);
                    setTtsVendor(tts);
                    setVoice(voiceModel);
                }



                setSelectedTel(agent.primary_tel);
                setSelectedMachine(agent.assigned_machine);
                var extractedTraits = extractTraits(agent.personality);
                setTraits(extractedTraits);
                setTemperature(agent.temperature);
                setModel(agent.model);
                setVoiceSpeed(agent.voice_speed);
                setAvatarType(agent.avatar_type);
                setSelectedAvatar(JSON.parse(agent.avatar_details) || null);
                setHeygenKey(JSON.parse(agent.avatar_details)?.key || null);
                setGoogleEmail(agent.google_email);
                setSelectedKeys(JSON.parse(agent.api_keys) || null);
                setBargeInEnabled(agent.bargein_enabled === "1" ? true : false);
                setFlowData(JSON.parse(agent.skill_map) || null)




            }
        } catch (err) {
            console.error("Error fetching machines:", err);
            setError("Error loading machines. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch machines from API where agent_id is empty (null)
    const fetchMachines = async () => {
        setLoading(true);
        setError(null);
        let agentMachines = [];
        const queryParams = "agent_id=UNASSIGNED"; // Filter for machines without an assigned agent
        const query = "api/getmachines?" + queryParams;

        try {
            if (agentGuid) {
                const queryAgentMachines = `api/getmachines?agent_id=${agentGuid}`;
                const responseAgentMachines = await fetch(queryAgentMachines, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + token,
                    },
                });

                if (!responseAgentMachines.ok) {
                    throw new Error("Failed to fetch machines");
                }

                agentMachines = await responseAgentMachines.json();
            }
            const response = await fetch(query, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch machines");
            }

            const data = await response.json();
            if (agentMachines.results?.length) {
                setMachines([...data.results, ...agentMachines.results, ""]);
            }

            else {
                setMachines([...data.results, ""] || []); // Assuming data contains `results`

            }
        } catch (err) {
            console.error("Error fetching machines:", err);
            setError("Error loading machines. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchTelephony = async () => {
        setLoading(true);
        setError(null);
        let agentTelephony = [];
        const queryParams = "&agent_id=UNASSIGNED"; // Filter for machines without an assigned agent
        const query = "api/getdatabase?table_name=telephony" + queryParams;

        try {
            if (agentGuid) {
                const queryAgentTelephony = `api/getdatabase?table_name=telephony&agent_id=${agentGuid}`;
                const responseAgentTelephony = await fetch(queryAgentTelephony, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + token,
                    },
                });

                if (!responseAgentTelephony.ok) {
                    throw new Error("Failed to fetch telephony");
                }

                agentTelephony = await responseAgentTelephony.json();
            }
            const response = await fetch(query, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch telephony");
            }

            const data = await response.json();
            if (agentTelephony.results?.length) {
                setTelephony([...data.results, ...agentTelephony.results, ""]);
            }

            else {
                setTelephony([...data.results, ""] || ['']); // Assuming data contains `results`

            }
        } catch (err) {
            console.error("Error fetching telephony:", err);
            setError("Error loading telephony. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    React.useEffect(() => {
        const fetchKeys = async () => {
            try {
                const response = await fetch("api/getdatabase?table_name=keys", {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + token,
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch API keys");

                const data = await response.json();
                setApiKeys(data.results || []);
            } catch (err) {
                console.error("API key fetch error:", err);
            }
        };

        fetchKeys();
    }, []);

    const handleFinish = (authGoogle = false) => {
        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token,
            },
            body: JSON.stringify({
                name: agentName,
                job_description: jobDescription,
                system_prompt: systemPrompt,
                title,
                personality: `Humor level ${traits.Humor}/100, Formality level ${traits.Formality}/100, Empathy level ${traits.Empathy}/100`,
                picture: faceImage,
                language,
                voice: `${sttVendor}_${ttsVendor}_${voice}`,
                voice_speed: voiceSpeed,
                assigned_machine: selectedMachine,
                primary_tel: selectedTel,
                guid: agentGuid ? agentGuid : null,
                temperature: temperature.toString(),
                model,
                avatar_type: avatarType,
                avatar_details: JSON.stringify(selectedAvatar),
                api_keys: JSON.stringify(selectedKeys),
                bargein_enabled: bargeInEnabled,
                skill_map: JSON.stringify(flowData),



            }),
        };

        fetch(agentGuid ? 'api/updateagent' : 'api/createagent', requestOptions)
            .then((response) => {
                if (!response.ok) {
                    // If response is not ok, throw an error to trigger catch block
                    throw new Error(`Failed to create agent: ${response.status} ${response.statusText}`);
                }
                return response.text(); // Parse the response (optional)
            })
            .then((data) => {
                console.log("Agent created successfully:", data);
                if (!agentGuid) {
                    fetch("api/deployagentwidget", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "Bearer " + token,
                        },
                        body: JSON.stringify({
                            agent_guid: data,
                        }),
                    });
                }
                if (!agentGuid) {
                    handleDialogClose(); // Close the dialog only after a successful fetch

                }
                if (authGoogle) {
                    let agentForGoogle = agentGuid ? agentGuid : data;
                    window.location.href = `https://sophona.com/app-google-auth?agent_guid=${agentForGoogle}&tenant=${window.location.host}&token=a`;

                }
                else {
                    navigate('/agents', { state: { refresh: Date.now() } });
                }
            })
            .catch((error) => {
                console.error("Error creating agent:", error);
                alert("Failed to create agent. Please try again."); // User feedback
            });
    };

    const handleSelectHeygenAvatar = (avatar) => {
        setSelectedAvatar(avatar);
        setFaceImage(avatar.normal_preview);

    };

    const handleNext = () => {
        if (activeStep === 1 && !faceImage) {
            fetchGeneratedFace();
        }
        setActiveStep((prevStep) => steps[steps.indexOf(prevStep) + 1]);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => steps[steps.indexOf(prevStep) - 1]);
    };


    const PillSelector = ({ options, value, onChange }) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {options.map((opt) => {
                const selected = value === opt.value;
                return (
                    <Box
                        key={opt.value}
                        onClick={() => onChange(opt.value)}
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 99,
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 500,
                            border: selected
                                ? "1px solid #6B46C1"
                                : "1px solid rgba(255,255,255,0.15)",
                            bgcolor: selected ? "rgba(107,70,193,0.15)" : "transparent",
                            color: "#000",
                            transition: "0.2s",
                            "&:hover": {
                                borderColor: "#B794F4",
                                color: "#B794F4",
                            },
                        }}
                    >
                        {opt.label}
                    </Box>
                );
            })}
        </Box>
    );


    return (

        <Box disableEnforceFocus sx={{ width: "100%", padding: "2rem" }}>
            <GlassPanel>
            <Stepper activeStep={steps.indexOf(activeStep)} alternativeLabel>
                {steps.map((label) => (
                    (agentGuid ? <Step sx={{ cursor: "pointer" }} completed={true} onClick={() => setActiveStep(label)} key={label}>
                        <StepLabel
                            sx={{
                                cursor: "pointer",
                                borderBottom: activeStep === label ? "solid 5px  #6B46C1" : null, // Fioletowy kolor zgodny z Twoim motywem
                                borderRadius: activeStep === label ? "0px" : "2px", // Fioletowy kolor zgodny z Twoim motywem
                                color: activeStep === label ? "#ffffff" : "#000000", // Biały tekst na aktywnym tle
                                padding: "8px ", // Lepsza przestrzeń wokół tekstu
                                transition: "background-color 0.3s ease-in-out", // Płynne przejście kolorów
                                "&:hover": {
                                    borderBottom: "solid 2px  #6B46C1", // Trochę ciemniejszy fiolet przy hoverze
                                },
                            }}
                        >
                            {<div style={agentGuid && activeStep === label ? { color: "black", cursor: "pointer" } : null}>{label}</div>}
                        </StepLabel>
                    </Step> :
                        <Step key={label}>
                            <StepLabel>
                                {label}
                            </StepLabel>
                        </Step>)
                ))}
            </Stepper>
            </GlassPanel>
            {/* Step Content */}
            <Box sx={{ mt: 4 }}>

                {activeStep === "Name & Appearance" && (
                    <>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr",  // ⬅ jedna kolumna
                                gap: 3,
                                width: "100%",
                                maxWidth: 700,
                                margin: "0 auto",
                                mt: 2,
                            }}
                        >

                            <GlassPanel title="Agent Name & Title">

                        <Box textAlign="center">
                            <Box sx={{ display: "inline-grid", mt: 4 }}>
                                <TextField
                                    label="Agent Name"
                                    variant="outlined"

                                    value={agentName}
                                    onChange={(e) => setAgentName(e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="Title"
                                    variant="outlined"

                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                               




                            </Box>

                            </Box>
                        </GlassPanel>
                            <GlassPanel title="Agent Appearance">
                                <Box sx={{ display: "grid", justifyContent: "center", width:"100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                borderRadius: "12px",
                                border: "1px solid #ccc",
                                            overflow: "hidden",
                                            mt: 2,
                                            background:"white"

                            }}
                        >
                            {[
                                { value: "basic", label: "Basic" },
                                { value: "heygen", label: "Heygen" },
                            ].map((option) => (
                                <Box
                                    key={option.value}
                                    onClick={() => setAvatarType(option.value)}
                                    sx={{
                                        px: 8,
                                        py: 1,
                                        cursor: "pointer",
                                        background: avatarType === option.value ? "#6B46C1" : "white",
                                        color: avatarType === option.value ? "white" : "black",
                                        transition: "0.2s",
                                        "&:hover": {
                                            background: avatarType === option.value ? "#5A36A0" : "#f5f5f5",
                                        }
                                    }}
                                >
                                    {option.label}
                                </Box>
                            ))}
                        </Box>
                        {
                            avatarType === "basic" ?
                                <Box textAlign="center" sx={{ mt: 4 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={fetchGeneratedFace}
                                        disabled={!agentName}
                                    >
                                        Generate Face
                                    </Button>
                                    {faceImage && (
                                        <Card sx={{ margin: "5rem", maxWidth: 256, mt: 2, margin: "0 auto" }}>
                                            <CardMedia
                                                component="img"
                                                height="256"
                                                image={faceImage}
                                                alt={`AI face for ${agentName}`}
                                            />
                                        </Card>
                                    )}
                                </Box>


                                :
                                avatarType === "advanced" ?
                                    <Box sx={{ display: "inline-grid", mt: 4 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={fetchGeneratedFace}
                                            disabled={!agentName}
                                        >
                                            Generate Face
                                        </Button>
                                        {faceImage && (
                                            <Card sx={{ margin: "5rem", maxWidth: 256, mt: 2, margin: "0 auto" }}>
                                                <CardMedia
                                                    component="img"
                                                    height="256"
                                                    image={faceImage}
                                                    alt={`AI face for ${agentName}`}
                                                />
                                            </Card>
                                        )}
                                    </Box>
                                    :
                                    <Box textAlign="center" sx={{ display: "inherit", mt: 4 }}>
                                        <ApiKeySelector
                                            apiKeys={apiKeys}
                                            selectedKeys={selectedKeys}
                                            onChange={setSelectedKeys}
                                            title="Assign Heygen API Key"
                                            defaultVendor="Heygen"
                                        />

                                        {selectedKeys["Heygen"] && !selectedAvatar && <HeygenSelection setSelectedId={handleSelectHeygenAvatar} apiKey={selectedKeys["Heygen"]}></HeygenSelection>}
                                        {selectedAvatar &&
                                            <>
                                                <Card sx={{ margin: "1rem", width: 256, height: 256, placeSelf: "center", mt: 1, }}>

                                                    <CardMedia
                                                        component="img"
                                                        image={selectedAvatar.normal_preview}
                                                        height="90%"
                                                        sx={{ padding: "2% ", objectFit: "contain" }}
                                                        alt={`AI face for ${selectedAvatar.pose_name}`}
                                                    />
                                                    <Typography variant="p">{selectedAvatar.pose_name}</Typography>

                                                </Card>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => setSelectedAvatar(null)}
                                                    disabled={!agentName}
                                                >
                                                    Change Heygen Avatar
                                                </Button>
                                            </>
                                        }
                                    </Box>
                                    }
                                </Box>
                            </GlassPanel>
                        </Box>
                    </>

                )}

                {activeStep === "Personality Traits" && (

                    <GlassPanel title="Configure Personality">
                        {["Humor", "Formality", "Empathy"].map((trait) => (
                            <Box key={trait} sx={{ mb: 4 }}>
                                <Typography>
                                    {trait.charAt(0).toUpperCase() + trait.slice(1)}:{" "}
                                    {traits[trait]}
                                </Typography>
                                <NiceSlider
                                    value={traits[trait]}
                                    onChange={(value) => setTraits({ ...traits, [trait]: value.target.value })}
                                    minValue={0}
                                    maxValue={100}
                                    stepValue={1}
                                    labels={["Min", "Balanced", "High"]}
                                />

                              
                            </Box>
                        ))}
                    </GlassPanel>

                )}

                {activeStep === "Intelligence Settings" && (
                    <Box sx={{ display: "grid", gap: 3 }}>

                        {/* ============================================
            BIG JOB DESCRIPTION PANEL (dominant section)
        ============================================ */}
                        <GlassPanel title="Intelligence Settings">
                            <Box sx={{ display: "grid", gap: 2 }}>
                                <TextField
                                    label="Describe the Job"
                                    multiline
                                    fullWidth
                                    variant="outlined"
                                    value={jobDescription}
                                    onChange={(e) => setJobDescription(e.target.value)}
                                    InputProps={{
                                        inputComponent: TextareaAutosize,
                                        inputProps: { minRows: 6, maxRows: 12 }
                                    }}
                                    sx={{
                                        "& textarea": { resize: "none", fontSize: 15 },
                                    }}
                                />

                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Optional: Upload Orientation Document
                                </Typography>

                                <Box
                                    sx={{
                                        border: "1px dashed rgba(255,255,255,0.25)",
                                        borderRadius: 2,
                                        p: 2,
                                        cursor: "pointer",
                                        textAlign: "center",
                                        "&:hover": { borderColor: "rgba(255,255,255,0.5)" }
                                    }}
                                    onClick={() => document.getElementById("orientationFile").click()}
                                >
                                    <Typography variant="body2">
                                        {uploadedFile ? uploadedFile.name : "Click to upload .pdf / .docx file"}
                                    </Typography>
                                </Box>

                                <input
                                    id="orientationFile"
                                    type="file"
                                    accept=".pdf,.docx"
                                    style={{ display: "none" }}
                                    onChange={(e) => setUploadedFile(e.target.files[0])}
                                />
                            </Box>
                        </GlassPanel>

                        {/* ============================================
            SMALLER PANELS: MODEL + TEMPERATURE (2 columns)
        ============================================ */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 3,
                            }}
                        >
                            {/* MODEL */}
                            <GlassPanel title="LLM Model" dense>
                                <PillSelector
                                    options={llmModelArray.map((m) => ({ value: m, label: m }))}
                                    value={model}
                                    onChange={(val) => setModel(val)}
                                />
                            </GlassPanel>

                            {/* TEMPERATURE */}
                            <GlassPanel title="Configure Creativity">
                                {/* Pasek temperatury */}
                                <NiceSlider value={Math.round(temperature * 100)} onChange={(e) => {
                                    const v = Number(e.target.value) / 100;
                                    setTemperature(v);
                                }} minValue={0}
                                    maxValue={100}
                                    stepValue={1}></NiceSlider>

                                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                        Lower = more deterministic · Higher = more creative
                                </Typography>
                            </GlassPanel>
                       


                        </Box>

                        {/* ============================================
            ADVANCED SYSTEM INSTRUCTIONS
        ============================================ */}
                        <GlassPanel title="Advanced" dense>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                sx={{ mb: 2 }}
                            >
                                {showAdvanced ? "Hide advanced options" : "Show advanced options"}
                            </Button>

                            {showAdvanced && (
                                <TextField
                                    label="System Prompt (Optional)"
                                    multiline
                                    fullWidth
                                    variant="outlined"
                                    value={systemPrompt}
                                    onChange={(e) => setSystemPrompt(e.target.value)}
                                    InputProps={{
                                        inputComponent: TextareaAutosize,
                                        inputProps: { minRows: 4, maxRows: 10 },
                                    }}
                                    sx={{
                                        "& textarea": { resize: "none", fontSize: 14 }
                                    }}
                                />
                            )}
                        </GlassPanel>
                    </Box>
                )}

                {activeStep === "Voice Configuration" && (
                    <Box
                        sx={{
                            maxWidth: 900,
                            mx: "auto",
                            mt: 3,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 3,
                            p: 2,
                        }}
                    >
                        {/* LEFT COLUMN */}
                        <GlassPanel title="Primary Language" dense>
                            <Select
                                fullWidth
                                size="small"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                            >
                                {sortedLanguages.map((lang) => (
                                    <MenuItem key={lang} value={lang}>
                                        {getLanguageLabel(lang, languagesArray)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </GlassPanel>

                        <GlassPanel title="Voice Speed" dense>
                            <Select
                                fullWidth
                                size="small"
                                value={voiceSpeed}
                                onChange={(e) => setVoiceSpeed(e.target.value)}
                            >
                                {voiceSpeedArray.map((v) => (
                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                ))}
                            </Select>
                        </GlassPanel>

                                               

                        {/* RIGHT COLUMN */}
                        <GlassPanel title="Speech Detection Vendor (STT)" dense>
                            <PillSelector
                                options={Object.keys(voicesData).map((v) => ({ value: v, label: v }))}
                                value={sttVendor}
                                onChange={(e) => setSttVendor(e)}
                            />
                        </GlassPanel>

                        <GlassPanel title="Voice Model Vendor (TTS)" dense>
                            <PillSelector
                                options={Object.keys(voicesData).map((v) => ({ value: v, label: v }))}
                                value={ttsVendor}
                                onChange={(e) => setTtsVendor(e)}
                            />
                        </GlassPanel>

                        {/* FULL-WIDTH VOICE SELECT */}
                        <Box sx={{ gridColumn: "1 / -1" }}>
                            <GlassPanel title="Voice" dense>
                                <Box
                                    ref={voiceListRef}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 1.2,
                                        maxHeight: 220,
                                        overflowY: "auto",
                                        pr: 1,
                                    }}
                                >
                                    {/* 🔹 Główne głosy dla wybranego języka */}
                                    {availableVoices.map((v) => (
                                        <Box
                                            id={`voice-${v.value}`}
                                            key={v.value}
                                            onClick={() => {
                                                if (voiceListRef.current) {
                                                    setSavedScrollTop(voiceListRef.current.scrollTop);
                                                }
                                                setVoice(v.value);
                                            }}
                                            sx={{
                                                p: 1,
                                                borderRadius: 1.5,
                                                cursor: "pointer",
                                                border:
                                                    voice === v.value
                                                        ? "2px solid #6B46C1"
                                                        : "1px solid rgba(255,255,255,0.1)",
                                                bgcolor:
                                                    voice === v.value
                                                        ? "rgba(107,70,193,0.15)"
                                                        : "rgba(255,255,255,0.03)",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                fontSize: 13,
                                                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                                            }}
                                        >
                                            <span>{v.name}</span>
                                            <IconButton
                                                size="small"
                                                sx={{ ml: 1 }}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    playAudio(v.audioSrc);
                                                }}
                                            >
                                                <VolumeUp sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    {/* 🌍 Multilingual – zawsze doklejane dla Azure */}
                                    {ttsVendor === "Azure" &&
                                        voicesData[ttsVendor]?.["Multilingual"]?.map((v) => (
                                            <Box
                                                id={`voice-${v.value}`}
                                                key={"multi-" + v.value}
                                                onClick={() => {
                                                    if (voiceListRef.current) {
                                                        setSavedScrollTop(voiceListRef.current.scrollTop);
                                                    }
                                                    setVoice(v.value);
                                                }}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1.5,
                                                    cursor: "pointer",
                                                    border:
                                                        voice === v.value
                                                            ? "2px solid #6B46C1"
                                                            : "1px solid rgba(255,255,255,0.1)",
                                                    bgcolor:
                                                        voice === v.value
                                                            ? "rgba(107,70,193,0.15)"
                                                            : "rgba(255,255,255,0.03)",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    fontSize: 13,
                                                    "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                                                }}
                                            >
                                                <span>🌍 {v.name}</span>
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        playAudio(v.audioSrc);
                                                    }}
                                                >
                                                    <VolumeUp sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                </Box>
                            </GlassPanel>
                        </Box>

                        <GlassPanel title="Barge In" dense>
                            <Box sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
                                <Tooltip title="Enable barge in" placement="right" arrow>
                                    <FormControlLabel
                                        control={<Checkbox checked={bargeInEnabled} onChange={(e) => setBargeInEnabled(e.target.checked)} />}
                                        label="Enable barge in"
                                    />
                                </Tooltip>
                            </Box>
                        </GlassPanel>
                    </Box>
                )}


                {activeStep === "Skill Map" && (
                    <GlassPanel title="Agent Resources" dense>
                        <AgentFlowCreator token={token} flowData={flowData} setFlowData={setFlowData} jobDescription={jobDescription} aiMode={aiMode} setAiMode={setAiMode} />
                    </GlassPanel>
                )}
                {activeStep === "Assign Resources" && (
                    <GlassPanel title="Agent Resources" dense>

                    <Box textAlign="center" sx={{ justifyContent: "center", display: "grid", mb: 2 }}>

                        {error ? (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{ display: "inline-grid", mt: 4 }}>
                                    <Typography>Select Computer</Typography>
                                    <Select
                                            MenuProps={{
                                                disableAutoFocus: true,
                                                disableEnforceFocus: true,
                                                PaperProps: { tabIndex: undefined },
                                                MenuListProps: { autoFocusItem: false },
                                            }}
                                            SelectDisplayProps={{ tabIndex: -1 }}
                                        value={selectedMachine}
                                        onChange={(e) => e.target.value ? setSelectedMachine(e.target.value) : setSelectedMachine("")}
                                        displayEmpty
                                        sx={{ minWidth: 300 }}
                                    >
                                        {machines.map((machine) => (
                                            <MenuItem key={machine.id} value={machine.guid}>
                                                {machine.name || `Computer not assigned`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ display: "inline-grid", mt: 4 }}>
                                    <Typography>Select Telephone</Typography>
                                    <Select
                                            MenuProps={{
                                                disableAutoFocus: true,
                                                disableEnforceFocus: true,
                                                PaperProps: { tabIndex: undefined },
                                                MenuListProps: { autoFocusItem: false },
                                            }}
                                            SelectDisplayProps={{ tabIndex: -1 }}
                                        value={selectedTel}
                                        onChange={(e) => e.target.value ? setSelectedTel(e.target.value) : setSelectedTel("")}
                                        displayEmpty
                                        sx={{ minWidth: 300 }}
                                    >
                                        {telephony.map((tel) => (
                                            <MenuItem key={tel.id} value={tel.number}>
                                                {tel.number || `Telephone not assigned`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ display: "inline-grid", mt: 4 }}>
                                    <Typography>Google Account</Typography>

                                    <GoogleLoginButton agentGuid={agentGuid} email={googleEmail} handleFinish={handleFinish} />


                                    </Box>
                                    {/* ⭐ ADVANCED OPTIONS COLLAPSIBLE SECTION */}

                                    <ApiKeySelector
                                        apiKeys={apiKeys}
                                        selectedKeys={selectedKeys}
                                        onChange={setSelectedKeys}
                                        title="Assign own API Keys"
                                    />


                            </>

                        )}


                        </Box>
                    </GlassPanel>
                )}

                {loading && <Box sx={{ justifyContent: "center", margin: "1vw", display: "flex", mt: 4 }}><CircularProgress sx={{ mt: 10 }} /></Box>}
                {/* Navigation Buttons */}
                <Box sx={{ display: "flex", justifyContent: "space-around", mt: 4 }}>
                    {!agentGuid && steps.indexOf(activeStep) > 0 && (
                        <Button onClick={handleBack} variant="outlined">
                            Back
                        </Button>
                    )}

                    {!agentGuid && steps.indexOf(activeStep) < steps.length - 1 ? (
                        <Button onClick={handleNext} variant="contained" hidden={
                            (steps.indexOf(activeStep) === 0 && (!agentName || !title))
                            ||
                            (activeStep === "Intelligence Settings" && !jobDescription)}>
                            Next
                        </Button>
                    ) :
                        (!agentGuid &&
                            <Button onClick={() => handleFinish()} variant="contained" color="primary">
                                Finish
                            </Button>
                        )}
                    {agentGuid &&
                        <Button onClick={() => handleFinish()} variant="contained" color="primary">
                            Save
                        </Button>}
                </Box>
            </Box>
        </Box>
    );
}